/**
 * @file Gate event union (umbrella #1048 / #1058) — the typed payloads
 *       the `GateSub` channel carries, authored as Zod schemas in
 *       `shared/` so both wire ends validate against one source. The
 *       command-event fields lived as a plain TS type in
 *       `service/provider.ts` (`CommandEventPayload`); the wire schema
 *       has to live here because `shared/` sits below `service/` in the
 *       dependency graph (a wire schema both ends import can't reach
 *       into `service/`).
 * @module @labcraft/hypervisor/shared/events
 *
 * The `GateServerFrame` was `{type:'event', kind:string, payload:
 * z.unknown()}` — an untyped hole every consumer duck-typed
 * (`frame.kind === 'command'` off raw JSON). #1058 replaces
 * `z.unknown()` with a **per-kind discriminated union**: today the one
 * kind is `command`, so the union has one member, but adding a second
 * event kind (a mentor nudge, a lifecycle notice) is a new discriminated
 * member — the consumer's `switch (frame.kind)` then type-checks
 * exhaustively instead of trusting a string.
 */

import { z } from 'zod'

////////////////////////////////////////////////////////////////////////////////
// Command event — the OSC 133 completed-command payload (#627)
////////////////////////////////////////////////////////////////////////////////

/**
 * Wire payload of a `command` gate event: one completed command the
 * in-guest OSC 133 markers (#627) delimited on a Machine's pty. This is
 * the provider's `CommandEventPayload` **minus** the `id` (the machine
 * id scopes the fan-out, it isn't repeated in every frame). Timestamps
 * are ISO-8601 host time.
 *
 * @property cmd - The command line the learner ran (B → C text).
 * @property exit - Exit code reported by the D marker.
 * @property cwd - Working directory reported by the D marker.
 * @property startedAt - ISO-8601 host time the command began (C marker).
 * @property finishedAt - ISO-8601 host time the command finished (D marker).
 * @property outputSnippet - Bounded tail of the bytes printed between
 *   the C and D markers.
 */
export const CommandEventSchema = z.object({
  cmd: z.string(),
  exit: z.number().int(),
  cwd: z.string(),
  startedAt: z.string(),
  finishedAt: z.string(),
  outputSnippet: z.string(),
})

/** Parsed `CommandEvent` (wire payload, no `id`). */
export type CommandEvent = z.infer<typeof CommandEventSchema>

////////////////////////////////////////////////////////////////////////////////
// Gate event frame — per-kind discriminated union
////////////////////////////////////////////////////////////////////////////////

/**
 * A `command` gate event frame — the typed replacement for the old
 * `{kind:string, payload:z.unknown()}` shape. `kind` is the inner
 * discriminant; adding an event kind adds a member here.
 *
 * @property type - Literal `'event'` (outer discriminant).
 * @property kind - Literal `'command'` (inner discriminant).
 * @property payload - {@link CommandEventSchema}.
 */
export const CommandGateFrameSchema = z.object({
  type: z.literal('event'),
  kind: z.literal('command'),
  payload: CommandEventSchema,
})

/**
 * Server → client gate frames — a discriminated union over the event
 * kinds. One member (`command`) today; the union shape is what makes a
 * consumer's `switch (frame.kind)` exhaustive once a second kind lands.
 * Discriminated on the inner `kind` (every member shares `type:'event'`).
 */
export const GateEventFrameSchema = z.discriminatedUnion('kind', [
  CommandGateFrameSchema,
])

/** Parsed `GateEventFrame`. */
export type GateEventFrame = z.infer<typeof GateEventFrameSchema>
